import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useState} from "react";

import Banner from "@khanacademy/wonder-blocks-banner";
import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {sizing} from "@khanacademy/wonder-blocks-tokens";
import {BodyText} from "@khanacademy/wonder-blocks-typography";
import {useFlagOrCookie} from "@khan/analytics-growthbook";
import {
    createReauthUrl,
    linkThirdPartyAccount,
    type LoginType,
} from "@khan/auth-shared";
import Log, {Errors} from "@khan/logging";
import {redirect, useLocation} from "@khan/urls";

import {useApolloGqlClient} from "~/data-access/gql/use-apollo-gql-client.ts";

import {Section} from "../components/section/section.tsx";

import LinkAppleAccount from "./link-apple-account.tsx";
import LinkEmail from "./link-email.tsx";
import LinkFacebookAccount from "./link-facebook-account.tsx";
import LinkGoogleAccount from "./link-google-account.tsx";
import {LinkMicrosoftAccount} from "./link-microsoft-account.tsx";
import {removeThirdPartyAuth} from "./remove-third-party-auth.ts";

interface LinkedAccountsProps {
    age: number | null | undefined;
    authEmails: Array<string> | null | undefined;
    hasPassword: boolean;
    // If the user is a parent modifying their child's settings.
    // Please note that the child may be over 13.
    isModifyingChild: boolean;
    identifier: string | null | undefined;
    appleId: string | null | undefined;
    applePrimaryEmail: string | null | undefined;
    googlePrimaryEmail: string | null | undefined;
    facebookId: string | null | undefined;
    microsoftId: string | null | undefined;
    microsoftPrimaryEmail: string | null | undefined;
    isClasslinkUser: boolean;
    facebookPrimaryEmail: string | null | undefined;
}

const strings = {
    disconnectAriaLabel: (thirdParty: LoginType) =>
        t`Disconnect from ${thirdParty}`,
} as const;

const LinkedAccounts: React.FC<LinkedAccountsProps> = ({
    age,
    authEmails,
    hasPassword,
    isModifyingChild,
    identifier,
    appleId,
    applePrimaryEmail,
    googlePrimaryEmail,
    facebookId,
    isClasslinkUser,
    facebookPrimaryEmail,
    microsoftId,
    microsoftPrimaryEmail,
}) => {
    const [warningMessage, setWarningMessage] = useState<string | null>(null);

    const {gqlClient} = useApolloGqlClient();
    const location = useLocation();
    const shouldShowMicrosoft = useFlagOrCookie("microsoft-sso");

    const maybeRenderBanner = (): React.ReactNode => {
        if (!warningMessage) {
            return null;
        }

        return (
            <>
                <Banner
                    kind="warning"
                    text={warningMessage}
                    layout="floating"
                />
                <View style={{height: sizing.size_160}} />
            </>
        );
    };

    /**
     * Add a third-party provider using the new login flow.
     * @param {LoginType} loginType The third-party provider (e.g. facebook).
     * @param {string} token  The auth token sent by the provider.
     */
    const linkAccountWith = (loginType: LoginType, token: string) => {
        linkThirdPartyAccount(gqlClient, loginType, token)
            .then((response) => {
                // get redirect URL that will display the success
                // message.
                const reauthUrl = createReauthUrl({
                    loginType,
                    pathname: location.pathname,
                });
                redirect(reauthUrl);
            })
            .catch((error) => {
                Log.error(error, Errors.Internal);
                // get redirect URL that will display the error message.
                const reauthUrlError = createReauthUrl({
                    loginType,
                    pathname: location.pathname,
                    error: error.message,
                });

                redirect(reauthUrlError);
            });
    };

    const onDisconnectThirdParty = (thirdParty: LoginType) => {
        if (
            // eslint-disable-next-line no-alert -- allow alerts
            !confirm(t`Are you sure you want to disconnect this account?`)
        ) {
            return;
        }

        removeThirdPartyAuth(gqlClient, thirdParty).then((messageKey: string) =>
            redirect(`${location.pathname}?messageKey=${messageKey}`),
        );
    };

    const renderThirdPartyDisconnectButton = (
        thirdParty: LoginType,
    ): React.ReactNode => {
        return (
            <Button
                onClick={() => onDisconnectThirdParty(thirdParty)}
                kind="tertiary"
                size="small"
                aria-label={strings.disconnectAriaLabel(thirdParty)}
            >
                {t`Disconnect`}
            </Button>
        );
    };

    return (
        <View
            id="linked-accounts"
            style={styles.container}
            role="region"
            aria-label={t`Linked accounts settings`}
        >
            <Section
                header={t`Linked accounts`}
                children={
                    <View>
                        {maybeRenderBanner()}
                        <BodyText>
                            {t`These linked accounts can be used to log in to Khan Academy:`}
                        </BodyText>
                        <View tag="ul" style={styles.list}>
                            {!isModifyingChild && (
                                <>
                                    <li>
                                        <LinkGoogleAccount
                                            gqlClient={gqlClient}
                                            googlePrimaryEmail={
                                                googlePrimaryEmail
                                            }
                                            identifier={identifier}
                                            isClasslinkUser={isClasslinkUser}
                                            onDisconnect={() =>
                                                onDisconnectThirdParty("google")
                                            }
                                            setWarningMessage={
                                                setWarningMessage
                                            }
                                            facebookId={facebookId}
                                            appleId={appleId}
                                            microsoftId={microsoftId}
                                            location={location}
                                            renderThirdPartyDisconnectButton={
                                                renderThirdPartyDisconnectButton
                                            }
                                            linkAccountWith={linkAccountWith}
                                        />
                                    </li>
                                    <li>
                                        <LinkFacebookAccount
                                            gqlClient={gqlClient}
                                            facebookId={facebookId}
                                            facebookPrimaryEmail={
                                                facebookPrimaryEmail
                                            }
                                            identifier={identifier}
                                            isClasslinkUser={isClasslinkUser}
                                            setWarningMessage={
                                                setWarningMessage
                                            }
                                            googlePrimaryEmail={
                                                googlePrimaryEmail
                                            }
                                            appleId={appleId}
                                            microsoftId={microsoftId}
                                            renderThirdPartyDisconnectButton={
                                                renderThirdPartyDisconnectButton
                                            }
                                            linkAccountWith={linkAccountWith}
                                        />
                                    </li>
                                    <li>
                                        <LinkAppleAccount
                                            gqlClient={gqlClient}
                                            appleId={appleId}
                                            applePrimaryEmail={
                                                applePrimaryEmail
                                            }
                                            location={location}
                                            identifier={identifier}
                                            isClasslinkUser={isClasslinkUser}
                                            setWarningMessage={
                                                setWarningMessage
                                            }
                                            googlePrimaryEmail={
                                                googlePrimaryEmail
                                            }
                                            facebookId={facebookId}
                                            microsoftId={microsoftId}
                                            renderThirdPartyDisconnectButton={
                                                renderThirdPartyDisconnectButton
                                            }
                                        />
                                    </li>
                                    {shouldShowMicrosoft && (
                                        <li>
                                            <LinkMicrosoftAccount
                                                gqlClient={gqlClient}
                                                microsoftId={microsoftId}
                                                microsoftPrimaryEmail={
                                                    microsoftPrimaryEmail
                                                }
                                                location={location}
                                                identifier={identifier}
                                                isClasslinkUser={
                                                    isClasslinkUser
                                                }
                                                setWarningMessage={
                                                    setWarningMessage
                                                }
                                                appleId={appleId}
                                                googlePrimaryEmail={
                                                    googlePrimaryEmail
                                                }
                                                facebookId={facebookId}
                                                renderThirdPartyDisconnectButton={
                                                    renderThirdPartyDisconnectButton
                                                }
                                            />
                                        </li>
                                    )}
                                </>
                            )}
                            <li>
                                <LinkEmail
                                    age={age}
                                    authEmails={authEmails}
                                    hasPassword={hasPassword}
                                    isModifyingChild={isModifyingChild}
                                    identifier={identifier}
                                />
                            </li>
                        </View>
                    </View>
                }
            />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        gap: sizing.size_120,
        width: "100%",
        maxWidth: 800,
    },
    list: {
        paddingBlockStart: sizing.size_120,
        gap: sizing.size_120,
    },
});

export default LinkedAccounts;
